import { round2 } from "@/lib/document-builder/formulas/decimalMath";
import type { DocFormulaEvalResult } from "./docFormulaTypes";
import { DOC_FORMULA_UNRESOLVED_DISPLAY } from "./docFormulaTypes";

const IDENT_RE = /^[A-Za-z][A-Za-z0-9_]*$/;

type Token =
  | { kind: "number"; value: number }
  | { kind: "ident"; name: string }
  | { kind: "op"; op: "+" | "-" | "*" | "/" }
  | { kind: "lparen" }
  | { kind: "rparen" };

function tokenize(expression: string): Token[] | { error: string } {
  const tokens: Token[] = [];
  let i = 0;
  const src = expression.trim();

  while (i < src.length) {
    const ch = src[i]!;
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    if (ch === "(") {
      tokens.push({ kind: "lparen" });
      i++;
      continue;
    }
    if (ch === ")") {
      tokens.push({ kind: "rparen" });
      i++;
      continue;
    }
    if ("+-*/".includes(ch)) {
      tokens.push({ kind: "op", op: ch as "+" | "-" | "*" | "/" });
      i++;
      continue;
    }
    if (/[0-9.]/.test(ch)) {
      let j = i + 1;
      while (j < src.length && /[0-9.]/.test(src[j]!)) j++;
      const raw = src.slice(i, j);
      const value = Number(raw);
      if (!Number.isFinite(value)) return { error: `Invalid number: ${raw}` };
      tokens.push({ kind: "number", value });
      i = j;
      continue;
    }
    if (/[A-Za-z_]/.test(ch)) {
      let j = i + 1;
      while (j < src.length && /[A-Za-z0-9_]/.test(src[j]!)) j++;
      const name = src.slice(i, j);
      if (!IDENT_RE.test(name)) return { error: `Invalid identifier: ${name}` };
      tokens.push({ kind: "ident", name });
      i = j;
      continue;
    }
    return { error: `Unexpected character: ${ch}` };
  }

  return tokens;
}

class Parser {
  private pos = 0;
  private readonly tokens: Token[];
  private readonly namespace: Readonly<Record<string, number>>;

  constructor(tokens: Token[], namespace: Readonly<Record<string, number>>) {
    this.tokens = tokens;
    this.namespace = namespace;
  }

  parse(): { ok: true; value: number } | { ok: false; error: string } {
    const result = this.parseExpr();
    if (!result.ok) return result;
    if (this.pos < this.tokens.length) {
      return { ok: false, error: "Unexpected trailing tokens in expression" };
    }
    return result;
  }

  private parseExpr(): { ok: true; value: number } | { ok: false; error: string } {
    let left = this.parseTerm();
    if (!left.ok) return left;

    while (this.pos < this.tokens.length) {
      const tok = this.tokens[this.pos];
      if (!tok || tok.kind !== "op" || (tok.op !== "+" && tok.op !== "-")) break;
      this.pos++;
      const right = this.parseTerm();
      if (!right.ok) return right;
      left = {
        ok: true,
        value: round2(tok.op === "+" ? left.value + right.value : left.value - right.value),
      };
    }
    return left;
  }

  private parseTerm(): { ok: true; value: number } | { ok: false; error: string } {
    let left = this.parseFactor();
    if (!left.ok) return left;

    while (this.pos < this.tokens.length) {
      const tok = this.tokens[this.pos];
      if (!tok || tok.kind !== "op" || (tok.op !== "*" && tok.op !== "/")) break;
      this.pos++;
      const right = this.parseFactor();
      if (!right.ok) return right;
      if (tok.op === "/" && right.value === 0) {
        return { ok: false, error: "Division by zero" };
      }
      left = {
        ok: true,
        value: round2(tok.op === "*" ? left.value * right.value : left.value / right.value),
      };
    }
    return left;
  }

  private parseFactor(): { ok: true; value: number } | { ok: false; error: string } {
    const tok = this.tokens[this.pos];
    if (!tok) return { ok: false, error: "Expression is empty" };

    if (tok.kind === "number") {
      this.pos++;
      return { ok: true, value: round2(tok.value) };
    }

    if (tok.kind === "ident") {
      this.pos++;
      if (!(tok.name in this.namespace)) {
        return { ok: false, error: `Unknown value: ${tok.name}` };
      }
      return { ok: true, value: round2(this.namespace[tok.name]!) };
    }

    if (tok.kind === "lparen") {
      this.pos++;
      const inner = this.parseExpr();
      if (!inner.ok) return inner;
      const close = this.tokens[this.pos];
      if (!close || close.kind !== "rparen") {
        return { ok: false, error: "Missing closing parenthesis" };
      }
      this.pos++;
      return inner;
    }

    if (tok.kind === "op" && tok.op === "-") {
      this.pos++;
      const inner = this.parseFactor();
      if (!inner.ok) return inner;
      return { ok: true, value: round2(-inner.value) };
    }

    return { ok: false, error: "Expected number, variable, or parenthesis" };
  }
}

export function evaluateDocFormulaExpression(
  expression: string,
  namespace: Readonly<Record<string, number>>,
): DocFormulaEvalResult {
  const trimmed = expression.trim();
  if (!trimmed) {
    return { ok: false, error: "Expression is required" };
  }

  const tokens = tokenize(trimmed);
  if ("error" in tokens) {
    return { ok: false, error: tokens.error };
  }

  const parsed = new Parser(tokens, namespace).parse();
  if (!parsed.ok) return parsed;

  return {
    ok: true,
    value: parsed.value,
    display: formatDocFormulaNumber(parsed.value),
  };
}

export function formatDocFormulaNumber(value: number): string {
  return round2(value).toFixed(2);
}

export function formatDocFormulaResult(result: DocFormulaEvalResult | undefined): string {
  if (!result || !result.ok) return DOC_FORMULA_UNRESOLVED_DISPLAY;
  return result.display;
}
